import { useState, useEffect, useCallback } from 'react'
import { useConfirmDelete } from '../hooks/useConfirmDelete'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { assegnazioniCantiereApi, commesseApi, cantieriApi, tecniciApi, mezziApi, Mezzo } from '../services/api'
import {
  ChevronLeft, ChevronRight, Plus, Calendar, Users, X, MessageCircle, Mail,
  Pencil, Download, FileText, GripVertical, Clock, MapPin, Car, AlertTriangle,
  CheckCircle2, PlayCircle, Circle, Ban, Eye, LayoutGrid, List, Table2,
  ArrowRight, ArrowLeft, BarChart3
} from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import toast from 'react-hot-toast'
import {
  format, startOfMonth, endOfMonth, eachDayOfInterval, getDay, isSameDay,
  isSameMonth, startOfWeek, endOfWeek, addDays, subDays, addMonths, subMonths,
  parseISO, isWithinInterval, addWeeks, subWeeks
} from 'date-fns'
import { it } from 'date-fns/locale'
import AssegnazioneTimeline from '../components/AssegnazioneTimeline'
import {
  DndContext, DragOverlay, useDraggable, useDroppable,
  PointerSensor, useSensor, useSensors, DragEndEvent,
} from '@dnd-kit/core'
import { CSS } from '@dnd-kit/utilities'

// ─── Tipi ──────────────────────────────────────────────────────────────
interface Assegnazione {
  id: number
  commessa_id: number
  cantiere_id: number
  tecnico_id: number | null
  data_inizio: string
  data_fine?: string
  ruolo: string
  status: string
  note?: string
  ora_inizio?: string
  mezzo_id?: number
  commessa_nome?: string
  cantiere_nome?: string
  cantiere_indirizzo?: string
  tecnico_nome?: string
  mezzo_targa?: string
  mezzo_marca?: string
  mezzo_modello?: string
}

interface Commessa { id: number; numero_commessa: string; nome: string }
interface Cantiere { id: number; commessa_id: number; nome: string; indirizzo?: string }
interface Tecnico { id: number; nome: string; cognome: string }
interface CaricoTecnico {
  tecnico_id: number
  tecnico_nome: string
  num_assegnazioni: number
  giorni_totali: number
}

// ─── Stili stato ───────────────────────────────────────────────────────
const STATUS_META: Record<string, { label: string; bg: string; text: string; border: string; icon: any }> = {
  assegnato:  { label: 'Assegnato',  bg: 'bg-blue-50',    text: 'text-blue-700',    border: 'border-blue-200',    icon: Circle },
  in_corso:   { label: 'In corso',   bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', icon: PlayCircle },
  completato: { label: 'Completato', bg: 'bg-slate-100',  text: 'text-slate-600',   border: 'border-slate-200',    icon: CheckCircle2 },
  annullato:  { label: 'Annullato',  bg: 'bg-red-50',     text: 'text-red-500',     border: 'border-red-200',     icon: Ban },
}

const STATUS_CLS_CARD: Record<string, string> = {
  assegnato:  'border-l-blue-400 bg-blue-50/30',
  in_corso:   'border-l-emerald-400 bg-emerald-50/30',
  completato: 'border-l-slate-300 bg-slate-50/30',
  annullato:  'border-l-red-300 bg-red-50/30',
}

const emptyForm = {
  commessa_id: '', cantiere_id: '', tecnico_id: '',
  data_inizio: new Date().toISOString().split('T')[0],
  data_fine: '', ora_inizio: '', mezzo_id: '', ruolo: 'tecnico', note: '',
}

// ─── Card Draggable ────────────────────────────────────────────────────
function AssegnazioneCard({
  a, isAdmin, onEdit, onStatus, onDelete, onWhatsappTec, onWhatsappGrp, onEmail,
  onResize, compact = false,
}: {
  a: Assegnazione
  isAdmin: boolean
  onEdit: (a: Assegnazione) => void
  onStatus: (id: number, s: string) => void
  onDelete: (a: Assegnazione) => void
  onWhatsappTec: (id: number) => void
  onWhatsappGrp: (id: number) => void
  onEmail: (id: number) => void
  onResize?: (a: Assegnazione, direction: 'extend' | 'shrink') => void
  compact?: boolean
}) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `card-${a.id}`,
    data: { type: 'assegnazione', assegnazione: a },
    disabled: !isAdmin,
  })
  const style = transform ? { transform: CSS.Translate.toString(transform), zIndex: 50 } : undefined
  const meta = STATUS_META[a.status] || STATUS_META.assegnato
  const StatusIcon = meta.icon

  if (compact) {
    return (
      <div
        ref={setNodeRef}
        style={style}
        className={`rounded border border-l-4 ${STATUS_CLS_CARD[a.status] || ''} ${
          isDragging ? 'opacity-40 shadow-lg' : 'opacity-100'
        } shadow-sm p-1.5 cursor-grab active:cursor-grabbing`}
        {...attributes}
        {...listeners}
      >
        <div className="text-[10px] font-semibold text-gray-800 truncate">{a.commessa_nome || `#${a.commessa_id}`}</div>
        <div className="text-[9px] text-gray-500 truncate">{a.cantiere_nome || `#${a.cantiere_id}`}</div>
      </div>
    )
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`relative rounded border border-l-4 ${STATUS_CLS_CARD[a.status] || ''} ${
        isDragging ? 'opacity-40 shadow-lg' : 'opacity-100'
      } shadow-sm hover:shadow-md transition-shadow cursor-grab active:cursor-grabbing`}
      {...attributes}
      {...listeners}
    >
      <div className="p-2 space-y-1">
        <div className="flex items-center justify-between gap-1">
          <span className="text-[11px] font-semibold text-gray-800 truncate flex-1" title={a.commessa_nome}>
            {a.commessa_nome || `Commessa #${a.commessa_id}`}
          </span>
          <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${meta.bg} ${meta.text} border ${meta.border} flex items-center gap-0.5`}>
            <StatusIcon size={10} /> {meta.label}
          </span>
        </div>
        <div className="text-[10px] text-gray-500 truncate flex items-center gap-1">
          <MapPin size={9} /> {a.cantiere_nome || `Cantiere #${a.cantiere_id}`}
        </div>
        {a.ora_inizio && (
          <div className="text-[10px] text-gray-500 flex items-center gap-1">
            <Clock size={9} /> {a.ora_inizio}
          </div>
        )}
        {a.mezzo_targa && (
          <div className="text-[10px] text-gray-500 flex items-center gap-1">
            <Car size={9} /> {a.mezzo_targa}
          </div>
        )}
        {isAdmin && (
          <div className="flex items-center gap-1 pt-1 border-t border-gray-100 mt-1">
            <button onClick={() => onEdit(a)} className="p-0.5 hover:bg-amber-100 rounded text-amber-600" title="Modifica">
              <Pencil size={11} />
            </button>
            {a.status === 'assegnato' && (
              <button onClick={() => onStatus(a.id, 'in_corso')} className="p-0.5 hover:bg-emerald-100 rounded text-emerald-600" title="Avvia">
                <PlayCircle size={11} />
              </button>
            )}
            {a.status === 'in_corso' && (
              <button onClick={() => onStatus(a.id, 'completato')} className="p-0.5 hover:bg-slate-100 rounded text-slate-600" title="Completa">
                <CheckCircle2 size={11} />
              </button>
            )}
            <button onClick={() => onWhatsappTec(a.id)} className="p-0.5 hover:bg-emerald-100 rounded text-emerald-600" title="WhatsApp Tecnico">
              <MessageCircle size={11} />
            </button>
            <button onClick={() => onEmail(a.id)} className="p-0.5 hover:bg-blue-100 rounded text-blue-600" title="Email Tecnico">
              <Mail size={11} />
            </button>
            {onResize && (
              <>
                <button onClick={() => onResize(a, 'extend')} className="p-0.5 hover:bg-blue-100 rounded text-blue-600" title="Estendi di 1 giorno">
                  <ArrowRight size={11} />
                </button>
                <button onClick={() => onResize(a, 'shrink')} className="p-0.5 hover:bg-orange-100 rounded text-orange-600" title="Accorcia di 1 giorno">
                  <ArrowLeft size={11} />
                </button>
              </>
            )}
            <button onClick={() => onDelete(a)} className="p-0.5 hover:bg-red-100 rounded text-red-500 ml-auto" title="Elimina">
              <X size={11} />
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Cella Droppable ───────────────────────────────────────────────────
function BoardCell({
  day, tecnicoId, assegnazioni, isAdmin, children,
}: {
  day: Date
  tecnicoId: number
  assegnazioni: Assegnazione[]
  isAdmin: boolean
  children: React.ReactNode
}) {
  const { setNodeRef, isOver } = useDroppable({
    id: `cell-${tecnicoId}-${format(day, 'yyyy-MM-dd')}`,
    data: { type: 'cell', day, tecnicoId },
    disabled: !isAdmin,
  })
  const hasConflict = assegnazioni.filter(a => {
    const start = parseISO(a.data_inizio)
    const end = a.data_fine ? parseISO(a.data_fine) : start
    return isWithinInterval(day, { start, end })
  }).length > 1

  return (
    <div
      ref={setNodeRef}
      className={`min-h-[100px] p-1.5 rounded border transition-colors ${
        isOver ? 'bg-blue-50 border-blue-300' : hasConflict ? 'bg-red-50/50 border-red-200' : 'bg-white border-gray-200'
      }`}
    >
      {hasConflict && (
        <div className="flex items-center gap-1 text-[9px] text-red-500 mb-1" title="Sovrapposizione assegnazioni">
          <AlertTriangle size={9} /> Conflitto
        </div>
      )}
      <div className="space-y-1.5">{children}</div>
    </div>
  )
}

// ─── Coda Non Assegnati (Droppable) ────────────────────────────────────
function CodaNonAssegnati({
  assegnazioni, isAdmin, onEdit, onStatus, onDelete, onWhatsappTec, onWhatsappGrp, onEmail,
}: {
  assegnazioni: Assegnazione[]
  isAdmin: boolean
  onEdit: (a: Assegnazione) => void
  onStatus: (id: number, s: string) => void
  onDelete: (a: Assegnazione) => void
  onWhatsappTec: (id: number) => void
  onWhatsappGrp: (id: number) => void
  onEmail: (id: number) => void
}) {
  const { setNodeRef, isOver } = useDroppable({
    id: 'coda-non-assegnati',
    data: { type: 'coda' },
    disabled: !isAdmin,
  })

  return (
    <div
      ref={setNodeRef}
      className={`card border-2 border-dashed ${isOver ? 'border-blue-400 bg-blue-50' : 'border-gray-300 bg-gray-50'} p-3 mb-4`}
    >
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
          <AlertTriangle size={14} /> Coda Non Assegnati
          <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full">{assegnazioni.length}</span>
        </h3>
        {isAdmin && <span className="text-[10px] text-gray-400">Trascina qui per rimuovere assegnazione</span>}
      </div>
      {assegnazioni.length === 0 ? (
        <div className="text-xs text-gray-400 text-center py-4">Nessun task in coda</div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
          {assegnazioni.map(a => (
            <AssegnazioneCard
              key={a.id}
              a={a}
              isAdmin={isAdmin}
              onEdit={onEdit}
              onStatus={onStatus}
              onDelete={onDelete}
              onWhatsappTec={onWhatsappTec}
              onWhatsappGrp={onWhatsappGrp}
              onEmail={onEmail}
              compact
            />
          ))}
        </div>
      )}
    </div>
  )
}

// ─── Indicatore Carico Tecnico ─────────────────────────────────────────
function CaricoTecnicoBar({ tecnici, carico, weekStart, weekEnd }: {
  tecnici: Tecnico[]
  carico: CaricoTecnico[]
  weekStart: Date
  weekEnd: Date
}) {
  const giorniLavorativi = 5 // assumiamo 5 giorni settimanali
  return (
    <div className="card mb-4 p-3">
      <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
        <BarChart3 size={14} /> Carico Settimanale — {format(weekStart, 'dd/MM')} / {format(weekEnd, 'dd/MM')}
      </h3>
      <div className="space-y-2">
        {tecnici.map(tec => {
          const c = carico.find(x => x.tecnico_id === tec.id)
          const giorni = c ? c.giorni_totali : 0
          const pct = Math.min((giorni / giorniLavorativi) * 100, 100)
          const colore = pct > 100 ? 'bg-red-500' : pct > 80 ? 'bg-orange-400' : pct > 50 ? 'bg-blue-500' : 'bg-emerald-500'
          return (
            <div key={tec.id} className="flex items-center gap-3">
              <div className="w-28 text-xs text-gray-600 truncate">{tec.nome} {tec.cognome}</div>
              <div className="flex-1 h-4 bg-gray-100 rounded-full overflow-hidden">
                <div className={`h-full ${colore} transition-all`} style={{ width: `${Math.min(pct, 100)}%` }} />
              </div>
              <div className="w-16 text-[10px] text-gray-500 text-right">
                {giorni.toFixed(1)}g / {c?.num_assegnazioni || 0} task
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ─── Vista Board (Dispatch Board) ──────────────────────────────────────
function VistaBoard({
  assegnazioni, tecnici, currentWeek, setCurrentWeek, isAdmin,
  onEdit, onStatus, onDelete, onWhatsappTec, onWhatsappGrp, onEmail, onResize,
}: {
  assegnazioni: Assegnazione[]
  tecnici: Tecnico[]
  currentWeek: Date
  setCurrentWeek: (d: Date) => void
  isAdmin: boolean
  onEdit: (a: Assegnazione) => void
  onStatus: (id: number, s: string) => void
  onDelete: (a: Assegnazione) => void
  onWhatsappTec: (id: number) => void
  onWhatsappGrp: (id: number) => void
  onEmail: (id: number) => void
  onResize: (a: Assegnazione, d: 'extend' | 'shrink') => void
}) {
  const weekStart = startOfWeek(currentWeek, { weekStartsOn: 1 })
  const weekEnd = endOfWeek(currentWeek, { weekStartsOn: 1 })
  const days = eachDayOfInterval({ start: weekStart, end: weekEnd })

  const getAssegnazioniForCell = (tecnicoId: number, day: Date) =>
    assegnazioni.filter(a => {
      if (a.tecnico_id !== tecnicoId) return false
      const start = parseISO(a.data_inizio)
      const end = a.data_fine ? parseISO(a.data_fine) : start
      return isWithinInterval(day, { start, end })
    })

  return (
    <div className="card overflow-x-auto">
      <div className="flex items-center justify-between mb-4">
        <button onClick={() => setCurrentWeek(subWeeks(currentWeek, 1))} className="p-2 hover:bg-gray-100 rounded">
          <ChevronLeft size={20} />
        </button>
        <h2 className="text-lg font-bold">
          {format(weekStart, 'dd/MM')} – {format(weekEnd, 'dd/MM/yyyy')}
        </h2>
        <button onClick={() => setCurrentWeek(addWeeks(currentWeek, 1))} className="p-2 hover:bg-gray-100 rounded">
          <ChevronRight size={20} />
        </button>
      </div>
      <div className="min-w-[900px]">
        {/* Header giorni */}
        <div className="grid grid-cols-[140px_repeat(7,1fr)] gap-1 mb-1">
          <div className="text-xs font-semibold text-gray-400 py-2 px-2">TECNICO</div>
          {days.map(day => {
            const isToday = isSameDay(day, new Date())
            return (
              <div key={day.toISOString()} className={`text-center text-xs font-semibold py-2 rounded ${isToday ? 'bg-blue-100 text-blue-700' : 'text-gray-500'}`}>
                <div>{format(day, 'EEE', { locale: it })}</div>
                <div className={isToday ? 'text-blue-800' : ''}>{format(day, 'dd/MM')}</div>
              </div>
            )
          })}
        </div>
        {/* Righe tecnici */}
        {tecnici.map(tec => (
          <div key={tec.id} className="grid grid-cols-[140px_repeat(7,1fr)] gap-1 mb-1">
            <div className="flex items-center gap-2 px-2 py-2 bg-gray-50 rounded border border-gray-100">
              <div className="w-7 h-7 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-bold">
                {tec.nome[0]}{tec.cognome[0]}
              </div>
              <div className="text-xs font-medium text-gray-700 truncate">
                {tec.nome} {tec.cognome}
              </div>
            </div>
            {days.map(day => {
              const cellAssegnazioni = getAssegnazioniForCell(tec.id, day)
              return (
                <BoardCell
                  key={day.toISOString()}
                  day={day}
                  tecnicoId={tec.id}
                  assegnazioni={cellAssegnazioni}
                  isAdmin={isAdmin}
                >
                  {cellAssegnazioni.map(a => (
                    <AssegnazioneCard
                      key={a.id}
                      a={a}
                      isAdmin={isAdmin}
                      onEdit={onEdit}
                      onStatus={onStatus}
                      onDelete={onDelete}
                      onWhatsappTec={onWhatsappTec}
                      onWhatsappGrp={onWhatsappGrp}
                      onEmail={onEmail}
                      onResize={onResize}
                    />
                  ))}
                </BoardCell>
              )
            })}
          </div>
        ))}
      </div>
    </div>
  )
}

// ─── Vista Settimanale ─────────────────────────────────────────────────
function VistaSettimanale({ assegnazioni, currentMonth, setCurrentMonth }: {
  assegnazioni: Assegnazione[]
  currentMonth: Date
  setCurrentMonth: (d: Date) => void
}) {
  const weekStart = startOfWeek(currentMonth, { weekStartsOn: 1 })
  const weekEnd = endOfWeek(currentMonth, { weekStartsOn: 1 })
  const days = eachDayOfInterval({ start: weekStart, end: weekEnd })

  const getAssegnazioniForDay = (day: Date) =>
    assegnazioni.filter(a => {
      const start = parseISO(a.data_inizio)
      const end = a.data_fine ? parseISO(a.data_fine) : start
      return isWithinInterval(day, { start, end })
    })

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-4">
        <button onClick={() => setCurrentMonth(subWeeks(currentMonth, 1))} className="p-2 hover:bg-gray-100 rounded"><ChevronLeft size={20} /></button>
        <h2 className="text-xl font-bold">{format(weekStart, 'dd/MM')} – {format(weekEnd, 'dd/MM/yyyy')}</h2>
        <button onClick={() => setCurrentMonth(addWeeks(currentMonth, 1))} className="p-2 hover:bg-gray-100 rounded"><ChevronRight size={20} /></button>
      </div>
      <div className="grid grid-cols-7 gap-2">
        {days.map(day => {
          const dayAssegnazioni = getAssegnazioniForDay(day)
          const isToday = isSameDay(day, new Date())
          return (
            <div key={day.toISOString()} className={`min-h-32 rounded border p-2 ${isToday ? 'border-blue-400 bg-blue-50' : 'border-gray-200 bg-white'}`}>
              <div className={`text-xs font-semibold mb-2 ${isToday ? 'text-blue-600' : 'text-gray-500'}`}>
                {format(day, 'EEE dd/MM', { locale: it })}
              </div>
              <div className="space-y-1">
                {dayAssegnazioni.map(a => {
                  const meta = STATUS_META[a.status] || STATUS_META.assegnato
                  return (
                    <div key={a.id} className={`text-[10px] rounded px-1.5 py-0.5 truncate border ${meta.bg} ${meta.text} ${meta.border}`}
                      title={`${a.tecnico_nome || 'Non assegnato'} – ${a.commessa_nome}`}>
                      <span className="font-medium">{a.tecnico_nome?.split(' ')[0] || 'Non assegn.'}</span>
                      <span className="opacity-70 ml-1">› {a.commessa_nome || '#' + a.commessa_id}</span>
                    </div>
                  )
                })}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ─── Componente Principale ─────────────────────────────────────────────
export default function CalendarioAssegnazioni() {
  const { confirmDelete, ConfirmModal } = useConfirmDelete()
  const { user } = useAuth()
  const qc = useQueryClient()
  const isAdmin = ['admin', 'superadmin', 'dispatcher', 'responsabile_tecnico'].includes(user?.role || '')

  const [currentMonth, setCurrentMonth] = useState(new Date())
  const [currentWeek, setCurrentWeek] = useState(new Date())
  const [showForm, setShowForm] = useState(false)
  const [form, setForm] = useState(emptyForm)
  const [tecniciIds, setTecniciIds] = useState<string[]>([''])
  const [selectedTecnico, setSelectedTecnico] = useState('')
  const [view, setView] = useState<'board' | 'calendar' | 'week' | 'list' | 'timeline'>('board')
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().slice(0, 10))
  const [editingId, setEditingId] = useState<number | null>(null)
  const [editForm, setEditForm] = useState<Record<string, string>>({})
  const [filterCommessa, setFilterCommessa] = useState('')
  const [filterCantiere, setFilterCantiere] = useState('')
  const [filterStatus, setFilterStatus] = useState('')
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())
  const [activeDrag, setActiveDrag] = useState<Assegnazione | null>(null)

  const { data: assegnazioniRaw = [], isLoading } = useQuery<Assegnazione[]>({
    queryKey: ['assegnazioni', selectedTecnico, filterCommessa, filterCantiere, filterStatus],
    queryFn: () =>
      assegnazioniCantiereApi.list({
        tecnico_id: selectedTecnico || undefined,
        commessa_id: filterCommessa || undefined,
        cantiere_id: filterCantiere || undefined,
        status: filterStatus || undefined,
      }).then(r => r.data),
  })

  const assegnazioni = assegnazioniRaw

  // Assegnazioni senza tecnico (tecnico_id nullable lato DB) = coda non assegnati
  const codaNonAssegnati = assegnazioni.filter(a => a.tecnico_id == null)

  const { data: commesse = [] } = useQuery<Commessa[]>({
    queryKey: ['commesse-active'],
    queryFn: () => commesseApi.list().then(r => r.data),
  })

  const { data: cantieriAll = [] } = useQuery<Cantiere[]>({
    queryKey: ['cantieri-all-list'],
    queryFn: () => cantieriApi.list().then(r => r.data),
  })

  const { data: tecniciData } = useQuery({
    queryKey: ['tecnici-select'],
    queryFn: () => tecniciApi.list({ per_page: 100, is_active: true }).then(r => r.data.items),
  })
  const tecnici: Tecnico[] = tecniciData || []

  const { data: mezziData } = useQuery({
    queryKey: ['mezzi-select'],
    queryFn: () => mezziApi.list(true).then(r => r.data),
  })
  const mezzi: Mezzo[] = mezziData || []

  // Carico tecnici per la settimana corrente
  const weekStart = startOfWeek(currentWeek, { weekStartsOn: 1 })
  const weekEnd = endOfWeek(currentWeek, { weekStartsOn: 1 })
  const { data: caricoData } = useQuery<CaricoTecnico[]>({
    queryKey: ['carico-tecnici', format(weekStart, 'yyyy-MM-dd'), format(weekEnd, 'yyyy-MM-dd')],
    queryFn: () => assegnazioniCantiereApi.caricoTecnici(
      format(weekStart, 'yyyy-MM-dd'),
      format(weekEnd, 'yyyy-MM-dd')
    ).then(r => r.data),
    enabled: view === 'board',
  })
  const carico: CaricoTecnico[] = caricoData || []

  const cantieriFiltrati = form.commessa_id
    ? cantieriAll.filter(c => c.commessa_id === parseInt(form.commessa_id))
    : []

  useEffect(() => {
    if (cantieriFiltrati.length === 1) {
      setForm(p => ({ ...p, cantiere_id: String(cantieriFiltrati[0].id) }))
    } else if (cantieriFiltrati.length === 0) {
      setForm(p => ({ ...p, cantiere_id: '' }))
    }
  }, [form.commessa_id]) // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Mutations ───────────────────────────────────────────────────────
  const createMut = useMutation({
    mutationFn: () => {
      const ids = tecniciIds.filter(Boolean)
      const base = {
        ...form,
        commessa_id: parseInt(form.commessa_id),
        cantiere_id: parseInt(form.cantiere_id),
        data_fine: form.data_fine || null,
        ora_inizio: form.ora_inizio || null,
        mezzo_id: form.mezzo_id ? parseInt(form.mezzo_id) : null,
      }
      // Nessun tecnico selezionato: crea un'unica assegnazione in coda non assegnati
      if (ids.length === 0) {
        return Promise.all([assegnazioniCantiereApi.create({ ...base, tecnico_id: null })])
      }
      return Promise.all(ids.map(tid =>
        assegnazioniCantiereApi.create({ ...base, tecnico_id: parseInt(tid) })
      ))
    },
    onSuccess: (results) => {
      qc.invalidateQueries({ queryKey: ['assegnazioni'] })
      qc.invalidateQueries({ queryKey: ['carico-tecnici'] })
      toast.success(`${results.length} assegnazion${results.length === 1 ? 'e creata' : 'i create'}`)
      setShowForm(false)
      setForm(emptyForm)
      setTecniciIds([''])
    },
    onError: (e: any) => {
      const msg = e?.response?.data?.detail || 'Errore creazione'
      if (e?.response?.status === 409) {
        toast.error(`⚠️ Conflitto: ${msg}`)
      } else {
        toast.error(msg)
      }
    },
  })

  const updateStatusMut = useMutation({
    mutationFn: ({ id, status }: { id: number; status: string }) =>
      assegnazioniCantiereApi.update(id, { status }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['assegnazioni'] }); toast.success('Stato aggiornato') },
    onError: () => toast.error('Errore aggiornamento'),
  })

  const deleteMut = useMutation({
    mutationFn: (id: number) => assegnazioniCantiereApi.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['assegnazioni'] })
      qc.invalidateQueries({ queryKey: ['carico-tecnici'] })
      toast.success('Assegnazione eliminata')
    },
    onError: () => toast.error('Errore eliminazione'),
  })

  const bulkNotificaMut = useMutation({
    mutationFn: (ids: number[]) => assegnazioniCantiereApi.inviaNotificheBulk(ids),
    onSuccess: (res: any) => {
      toast.success(`Notifiche inviate: ${res.data.inviate}, errori: ${res.data.errori?.length || 0}`)
      qc.invalidateQueries({ queryKey: ['assegnazioni'] })
      setSelectedIds(new Set())
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Errore invio notifiche'),
  })

  const bulkDeleteMut = useMutation({
    mutationFn: async (ids: number[]) => {
      for (const id of ids) await assegnazioniCantiereApi.delete(id)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['assegnazioni'] })
      qc.invalidateQueries({ queryKey: ['carico-tecnici'] })
      toast.success('Assegnazioni eliminate')
      setSelectedIds(new Set())
    },
    onError: () => toast.error('Errore eliminazione multipla'),
  })

  const updateMut = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Record<string, unknown> }) =>
      assegnazioniCantiereApi.update(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['assegnazioni'] })
      qc.invalidateQueries({ queryKey: ['carico-tecnici'] })
      toast.success('Assegnazione aggiornata')
      setEditingId(null)
      setEditForm({})
    },
    onError: (e: any) => {
      const msg = e?.response?.data?.detail || 'Errore aggiornamento'
      if (e?.response?.status === 409) {
        toast.error(`⚠️ Conflitto: ${msg}`)
      } else {
        toast.error(msg)
      }
    },
  })

  const whatsappTecnicoMut = useMutation({
    mutationFn: (id: number) => assegnazioniCantiereApi.inviaWhatsappTecnico(id),
    onSuccess: (res) => res.data.inviato ? toast.success('WhatsApp inviato al tecnico') : toast.error(res.data.motivo || 'Invio non riuscito'),
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Errore invio WhatsApp'),
  })

  const whatsappGruppoMut = useMutation({
    mutationFn: (id: number) => assegnazioniCantiereApi.inviaWhatsappGruppo(id),
    onSuccess: (res) => res.data.inviato ? toast.success('WhatsApp inviato al gruppo') : toast.error(res.data.motivo || 'Invio non riuscito'),
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Errore invio WhatsApp gruppo'),
  })

  const emailTecnicoMut = useMutation({
    mutationFn: (id: number) => assegnazioniCantiereApi.inviaEmailTecnico(id),
    onSuccess: (res) => res.data.inviato ? toast.success('Email inviata al tecnico') : toast.error(res.data.motivo || 'Invio non riuscito'),
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Errore invio email'),
  })

  // ─── Resize assegnazione ─────────────────────────────────────────────
  const handleResize = useCallback((a: Assegnazione, direction: 'extend' | 'shrink') => {
    const currentFine = a.data_fine ? parseISO(a.data_fine) : parseISO(a.data_inizio)
    const newFine = direction === 'extend'
      ? addDays(currentFine, 1)
      : subDays(currentFine, 1)
    // Non permettere di accorciare sotto la data_inizio
    if (newFine < parseISO(a.data_inizio)) {
      toast.error('Non può essere più corta di 1 giorno')
      return
    }
    updateMut.mutate({
      id: a.id,
      data: { data_fine: format(newFine, 'yyyy-MM-dd') },
    })
  }, [updateMut])

  // ─── Drag & Drop ─────────────────────────────────────────────────────
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  )

  const handleDragStart = useCallback((event: any) => {
    const { active } = event
    if (active.data.current?.type === 'assegnazione') {
      setActiveDrag(active.data.current.assegnazione)
    }
  }, [])

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event
    setActiveDrag(null)
    if (!over) return

    const activeData = active.data.current
    const overData = over.data.current
    if (!activeData || !overData) return
    if (activeData.type !== 'assegnazione') return

    const assegnazione: Assegnazione = activeData.assegnazione
    const payload: Record<string, unknown> = {}

    if (overData.type === 'cell') {
      const newDay: Date = overData.day
      const newTecnicoId: number = overData.tecnicoId
      const newDateStr = format(newDay, 'yyyy-MM-dd')

      if (newTecnicoId !== assegnazione.tecnico_id) {
        payload.tecnico_id = newTecnicoId
      }
      if (newDateStr !== assegnazione.data_inizio) {
        payload.data_inizio = newDateStr
        if (assegnazione.data_fine === assegnazione.data_inizio) {
          payload.data_fine = newDateStr
        }
      }
    } else if (overData.type === 'coda') {
      // Trascinato nella coda non assegnati: rimuove il tecnico assegnato
      if (assegnazione.tecnico_id == null) return
      payload.tecnico_id = null
    }

    if (Object.keys(payload).length > 0) {
      updateMut.mutate({ id: assegnazione.id, data: payload })
    }
  }, [updateMut])

  // ─── Helpers UI ──────────────────────────────────────────────────────
  const toggleSelect = (id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleSelectAll = () => {
    if (selectedIds.size === assegnazioni.length) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(assegnazioni.map(a => a.id)))
    }
  }

  const handleStartEdit = (a: Assegnazione) => {
    setEditingId(a.id)
    setEditForm({
      commessa_id: String(a.commessa_id),
      cantiere_id: String(a.cantiere_id),
      tecnico_id: String(a.tecnico_id),
      data_inizio: a.data_inizio,
      data_fine: a.data_fine || '',
      ora_inizio: a.ora_inizio || '',
      mezzo_id: a.mezzo_id ? String(a.mezzo_id) : '',
      note: a.note || '',
    })
  }

  const handleSaveEdit = (id: number) => {
    const payload: Record<string, unknown> = {}
    if (editForm.data_inizio) payload.data_inizio = editForm.data_inizio
    if (editForm.data_fine) payload.data_fine = editForm.data_fine
    if (editForm.ora_inizio) payload.ora_inizio = editForm.ora_inizio
    if (editForm.mezzo_id) payload.mezzo_id = parseInt(editForm.mezzo_id)
    if (editForm.note !== undefined) payload.note = editForm.note
    updateMut.mutate({ id, data: payload })
  }

  // ─── Calendario mensile helpers ──────────────────────────────────────
  const monthStart = startOfMonth(currentMonth)
  const monthEnd = endOfMonth(currentMonth)
  const days = eachDayOfInterval({ start: monthStart, end: monthEnd })
  const startPad = (getDay(monthStart) + 6) % 7
  const paddedDays = [...Array(startPad).fill(null), ...days]

  const getAssegnazioniForDay = (day: Date) =>
    assegnazioni.filter(a => {
      const start = parseISO(a.data_inizio)
      const end = a.data_fine ? parseISO(a.data_fine) : start
      return isWithinInterval(day, { start, end })
    })

  // ─── Render ──────────────────────────────────────────────────────────
  return (
    <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
      <div className="p-6 max-w-7xl mx-auto">
        {ConfirmModal}

        {/* Header */}
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Users size={28} className="text-blue-600" />
            <h1 className="text-2xl font-bold text-gray-800">Assegnazioni Cantieri</h1>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {isAdmin && (
              <>
                <select value={selectedTecnico} onChange={e => setSelectedTecnico(e.target.value)} className="input text-sm py-1">
                  <option value="">Tutti i tecnici</option>
                  {tecnici.map(t => <option key={t.id} value={t.id}>{t.nome} {t.cognome}</option>)}
                </select>
                <select value={filterCommessa} onChange={e => { setFilterCommessa(e.target.value); setFilterCantiere('') }} className="input text-sm py-1">
                  <option value="">Tutte le commesse</option>
                  {commesse.map(c => <option key={c.id} value={c.id}>{c.numero_commessa}</option>)}
                </select>
                <select value={filterCantiere} onChange={e => setFilterCantiere(e.target.value)} className="input text-sm py-1" disabled={!filterCommessa}>
                  <option value="">Tutti i cantieri</option>
                  {cantieriAll.filter(c => !filterCommessa || c.commessa_id === parseInt(filterCommessa)).map(c => (
                    <option key={c.id} value={c.id}>{c.nome}</option>
                  ))}
                </select>
                <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="input text-sm py-1">
                  <option value="">Tutti gli stati</option>
                  <option value="assegnato">Assegnato</option>
                  <option value="in_corso">In corso</option>
                  <option value="completato">Completato</option>
                  <option value="annullato">Annullato</option>
                </select>
              </>
            )}

            <button className={view === 'board' ? 'btn-primary text-sm' : 'btn-secondary text-sm'} onClick={() => setView('board')} title="Board tecnici">
              <LayoutGrid size={14} />
            </button>
            <button className={view === 'calendar' ? 'btn-primary text-sm' : 'btn-secondary text-sm'} onClick={() => setView('calendar')} title="Calendario mensile">
              <Calendar size={14} />
            </button>
            <button className={view === 'week' ? 'btn-primary text-sm' : 'btn-secondary text-sm'} onClick={() => setView('week')} title="Settimana">
              <Table2 size={14} />
            </button>
            <button className={view === 'list' ? 'btn-primary text-sm' : 'btn-secondary text-sm'} onClick={() => setView('list')} title="Lista">
              <List size={14} />
            </button>
            <button className={view === 'timeline' ? 'btn-primary text-sm' : 'btn-secondary text-sm'} onClick={() => setView('timeline')} title="Timeline">
              <Eye size={14} />
            </button>

            {isAdmin && (
              <button onClick={() => setShowForm(!showForm)} className="btn-primary flex items-center gap-2">
                <Plus size={16} /> Nuova
              </button>
            )}
            <button onClick={async () => {
              try {
                const res = await assegnazioniCantiereApi.reportPdf({
                  tecnico_id: selectedTecnico || undefined,
                  commessa_id: filterCommessa || undefined,
                  cantiere_id: filterCantiere || undefined,
                  status: filterStatus || undefined,
                })
                const blob = new Blob([res.data], { type: 'application/pdf' })
                const url = URL.createObjectURL(blob)
                const a = document.createElement('a')
                a.href = url
                a.download = `report_assegnazioni_${new Date().toISOString().slice(0,10)}.pdf`
                a.click()
                URL.revokeObjectURL(url)
                toast.success('Report PDF scaricato')
              } catch (e: any) {
                toast.error(e?.response?.data?.detail || 'Errore generazione PDF')
              }
            }} className="btn-secondary flex items-center gap-2 text-sm">
              <FileText size={15} /> PDF
            </button>
            <button onClick={() => {
              const rows = assegnazioni.map(a => ({
                ID: a.id, Commessa: a.commessa_nome || a.commessa_id,
                Cantiere: a.cantiere_nome || a.cantiere_id, Tecnico: a.tecnico_nome || a.tecnico_id,
                'Data Inizio': a.data_inizio, 'Data Fine': a.data_fine || '', Ruolo: a.ruolo, Stato: a.status,
              }))
              if (rows.length === 0) { toast.error('Nessuna assegnazione da esportare'); return }
              const headers = Object.keys(rows[0])
              const csv = [headers.join(';'), ...rows.map(r => headers.map(h => String(r[h as keyof typeof r] || '')).join(';'))].join('\n')
              const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
              const url = URL.createObjectURL(blob)
              const a = document.createElement('a')
              a.href = url; a.download = `assegnazioni_${new Date().toISOString().slice(0,10)}.csv`; a.click()
              URL.revokeObjectURL(url)
              toast.success('Export CSV completato')
            }} className="btn-secondary flex items-center gap-2 text-sm">
              <Download size={15} /> CSV
            </button>
          </div>
        </div>

        {/* Form nuova assegnazione */}
        {showForm && (
          <div className="card mb-6">
            <h2 className="text-lg font-semibold mb-4">Nuova Assegnazione</h2>
            <form onSubmit={e => { e.preventDefault(); createMut.mutate() }} className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Commessa *</label>
                <select className="input w-full" value={form.commessa_id}
                  onChange={e => setForm(p => ({ ...p, commessa_id: e.target.value, cantiere_id: '' }))} required>
                  <option value="">Seleziona...</option>
                  {commesse.map(c => <option key={c.id} value={c.id}>{c.numero_commessa}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Cantiere *</label>
                {!form.commessa_id ? (
                  <input className="input w-full bg-slate-50 text-slate-400" disabled placeholder="Seleziona prima una commessa" />
                ) : cantieriFiltrati.length === 0 ? (
                  <input className="input w-full bg-slate-50 text-slate-400" disabled placeholder="Nessun cantiere disponibile" />
                ) : cantieriFiltrati.length === 1 ? (
                  <input className="input w-full bg-slate-100 text-slate-600 cursor-not-allowed" readOnly value={cantieriFiltrati[0].nome} />
                ) : (
                  <select className="input w-full" value={form.cantiere_id}
                    onChange={e => setForm(p => ({ ...p, cantiere_id: e.target.value }))} required>
                    <option value="">Seleziona...</option>
                    {cantieriFiltrati.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
                  </select>
                )}
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium mb-1">Tecnici</label>
                <p className="text-xs text-gray-400 mb-1">Lascia vuoto per creare un task in coda, da assegnare in seguito trascinandolo sulla board</p>
                <div className="space-y-2">
                  {tecniciIds.map((tid, idx) => (
                    <div key={idx} className="flex gap-2 items-center">
                      <select className="input flex-1" value={tid}
                        onChange={e => setTecniciIds(prev => prev.map((v, i) => i === idx ? e.target.value : v))}>
                        <option value="">Nessun tecnico (coda)...</option>
                        {tecnici.map(t => <option key={t.id} value={t.id}>{t.nome} {t.cognome}</option>)}
                      </select>
                      {tecniciIds.length > 1 && (
                        <button type="button" onClick={() => setTecniciIds(prev => prev.filter((_, i) => i !== idx))}
                          className="text-slate-400 hover:text-red-500 p-1"><X size={15} /></button>
                      )}
                    </div>
                  ))}
                  <button type="button" onClick={() => setTecniciIds(prev => [...prev, ''])}
                    className="text-sm text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1">
                    <Plus size={13} /> Aggiungi tecnico
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Data Inizio *</label>
                <input type="date" className="input w-full" value={form.data_inizio}
                  onChange={e => setForm(p => ({ ...p, data_inizio: e.target.value }))} required />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Data Fine</label>
                <input type="date" className="input w-full" value={form.data_fine}
                  onChange={e => setForm(p => ({ ...p, data_fine: e.target.value }))} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Orario Inizio</label>
                <input type="time" className="input w-full" value={form.ora_inizio}
                  onChange={e => setForm(p => ({ ...p, ora_inizio: e.target.value }))} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Mezzo</label>
                <select className="input w-full" value={form.mezzo_id}
                  onChange={e => setForm(p => ({ ...p, mezzo_id: e.target.value }))}>
                  <option value="">Nessun mezzo</option>
                  {mezzi.map(m => <option key={m.id} value={m.id}>{m.targa}{m.modello ? ` - ${m.modello}` : ''}</option>)}
                </select>
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium mb-1">Note</label>
                <input className="input w-full" value={form.note}
                  onChange={e => setForm(p => ({ ...p, note: e.target.value }))} placeholder="Note facoltative" />
              </div>
              <div className="col-span-2 flex justify-end gap-3">
                <button type="button" className="btn-secondary"
                  onClick={() => { setShowForm(false); setForm(emptyForm); setTecniciIds(['']) }}>Annulla</button>
                <button type="submit" className="btn-primary" disabled={createMut.isPending}>Crea</button>
              </div>
            </form>
          </div>
        )}

        {/* Barra selezione multipla */}
        {selectedIds.size > 0 && isAdmin && (
          <div className="flex items-center gap-3 mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
            <span className="text-sm font-medium text-blue-800">{selectedIds.size} selezionate</span>
            <button onClick={() => bulkNotificaMut.mutate(Array.from(selectedIds))} disabled={bulkNotificaMut.isPending}
              className="text-xs bg-emerald-50 text-emerald-700 border border-emerald-300 px-3 py-1.5 rounded hover:bg-emerald-100 flex items-center gap-1">
              <MessageCircle size={13} /> Notifica Bulk
            </button>
            <button onClick={() => confirmDelete(`Eliminare ${selectedIds.size} assegnazioni?`, () => bulkDeleteMut.mutate(Array.from(selectedIds)))}
              className="text-xs bg-red-50 text-red-700 border border-red-300 px-3 py-1.5 rounded hover:bg-red-100">
              Elimina Selezionate
            </button>
            <button onClick={() => setSelectedIds(new Set())} className="text-xs text-gray-500 hover:text-gray-700">Deseleziona tutto</button>
          </div>
        )}

        {/* Contenuto in base alla vista */}
        {isLoading ? (
          <div className="text-center py-12 text-gray-500">Caricamento...</div>
        ) : view === 'timeline' ? (
          <AssegnazioneTimeline data={selectedDate} onDataChange={setSelectedDate} />
        ) : view === 'board' ? (
          <>
            {/* Carico tecnici */}
            <CaricoTecnicoBar tecnici={tecnici} carico={carico} weekStart={weekStart} weekEnd={weekEnd} />
            {/* Coda non assegnati */}
            <CodaNonAssegnati
              assegnazioni={codaNonAssegnati}
              isAdmin={isAdmin}
              onEdit={handleStartEdit}
              onStatus={(id, s) => updateStatusMut.mutate({ id, status: s })}
              onDelete={(a) => confirmDelete('Eliminare?', () => deleteMut.mutate(a.id))}
              onWhatsappTec={(id) => whatsappTecnicoMut.mutate(id)}
              onWhatsappGrp={(id) => whatsappGruppoMut.mutate(id)}
              onEmail={(id) => emailTecnicoMut.mutate(id)}
            />
            <VistaBoard
              assegnazioni={assegnazioni}
              tecnici={tecnici}
              currentWeek={currentWeek}
              setCurrentWeek={setCurrentWeek}
              isAdmin={isAdmin}
              onEdit={handleStartEdit}
              onStatus={(id, s) => updateStatusMut.mutate({ id, status: s })}
              onDelete={(a) => confirmDelete('Eliminare?', () => deleteMut.mutate(a.id))}
              onWhatsappTec={(id) => whatsappTecnicoMut.mutate(id)}
              onWhatsappGrp={(id) => whatsappGruppoMut.mutate(id)}
              onEmail={(id) => emailTecnicoMut.mutate(id)}
              onResize={handleResize}
            />
          </>
        ) : view === 'week' ? (
          <VistaSettimanale assegnazioni={assegnazioni} currentMonth={currentMonth} setCurrentMonth={setCurrentMonth} />
        ) : view === 'calendar' ? (
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <button onClick={() => setCurrentMonth(m => subMonths(m, 1))} className="p-2 hover:bg-gray-100 rounded"><ChevronLeft size={20} /></button>
              <h2 className="text-xl font-bold capitalize">{format(currentMonth, 'MMMM yyyy', { locale: it })}</h2>
              <button onClick={() => setCurrentMonth(m => addMonths(m, 1))} className="p-2 hover:bg-gray-100 rounded"><ChevronRight size={20} /></button>
            </div>
            <div className="grid grid-cols-7 gap-1 mb-2">
              {['Lun','Mar','Mer','Gio','Ven','Sab','Dom'].map(d => (
                <div key={d} className="text-center text-xs font-semibold text-gray-500 py-2">{d}</div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {paddedDays.map((day, idx) => {
                if (!day) return <div key={idx} />
                const dayAssegnazioni = getAssegnazioniForDay(day)
                const isToday = isSameDay(day, new Date())
                return (
                  <div key={idx} className={`min-h-20 p-1 rounded border text-xs ${isToday ? 'border-blue-400 bg-blue-50' : 'border-gray-200 bg-white'}`}>
                    <div className={`font-semibold mb-1 ${isToday ? 'text-blue-600' : 'text-gray-700'}`}>{format(day, 'd')}</div>
                    <div className="space-y-0.5">
                      {dayAssegnazioni.slice(0, 3).map(a => {
                        const meta = STATUS_META[a.status] || STATUS_META.assegnato
                        return (
                          <div key={a.id} className={`truncate rounded px-1 text-[10px] ${meta.bg} ${meta.text} ${meta.border} border`}
                            title={`${a.tecnico_nome || 'Non assegnato'} – ${a.commessa_nome}`}>
                            {a.tecnico_nome?.split(' ')[0] || 'Non assegn.'}
                          </div>
                        )
                      })}
                      {dayAssegnazioni.length > 3 && <div className="text-gray-400">+{dayAssegnazioni.length - 3}</div>}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        ) : (
          /* Vista lista */
          <div className="space-y-3">
            {assegnazioni.length === 0 ? (
              <div className="card text-center py-12 text-gray-500">Nessuna assegnazione trovata</div>
            ) : (
              <>
                {isAdmin && (
                  <div className="flex items-center gap-2 mb-2">
                    <input type="checkbox" checked={selectedIds.size === assegnazioni.length && assegnazioni.length > 0}
                      onChange={toggleSelectAll} className="w-4 h-4" />
                    <span className="text-xs text-gray-500">Seleziona tutto</span>
                  </div>
                )}
                {assegnazioni.map(a => {
                  const meta = STATUS_META[a.status] || STATUS_META.assegnato
                  return (
                    <div key={a.id} className={`card border-l-4 ${STATUS_CLS_CARD[a.status] || ''}`}>
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          {isAdmin && (
                            <input type="checkbox" checked={selectedIds.has(a.id)} onChange={() => toggleSelect(a.id)} className="w-4 h-4 mt-1" />
                          )}
                          <div>
                            <div className="flex items-center gap-3 mb-1">
                              <span className="font-semibold text-gray-800">{a.commessa_nome || `Commessa #${a.commessa_id}`}</span>
                              <span className="text-gray-400">›</span>
                              <span className="text-gray-700">{a.cantiere_nome || `Cantiere #${a.cantiere_id}`}</span>
                              <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${meta.bg} ${meta.text} border ${meta.border}`}>
                                {meta.label}
                              </span>
                            </div>
                            <div className="text-sm text-gray-600 flex gap-4 flex-wrap">
                              <span>Tecnico: <strong>{a.tecnico_nome || (a.tecnico_id ? `#${a.tecnico_id}` : 'Non assegnato')}</strong></span>
                              <span>Dal {format(parseISO(a.data_inizio), 'dd/MM/yyyy', { locale: it })}
                                {a.data_fine && ` al ${format(parseISO(a.data_fine), 'dd/MM/yyyy', { locale: it })}`}</span>
                              {a.ora_inizio && <span className="flex items-center gap-1"><Clock size={12} /> {a.ora_inizio}</span>}
                              {a.mezzo_targa && <span className="flex items-center gap-1"><Car size={12} /> {a.mezzo_targa}</span>}
                            </div>
                          </div>
                        </div>
                        {isAdmin && (
                          <div className="flex items-center gap-2 flex-wrap justify-end">
                            {editingId === a.id ? (
                              <div className="flex items-center gap-2">
                                <input type="date" className="input text-xs py-1 px-2 w-32"
                                  value={editForm.data_inizio || ''} onChange={e => setEditForm(p => ({ ...p, data_inizio: e.target.value }))} />
                                <input type="date" className="input text-xs py-1 px-2 w-32"
                                  value={editForm.data_fine || ''} onChange={e => setEditForm(p => ({ ...p, data_fine: e.target.value }))} />
                                <input type="time" className="input text-xs py-1 px-2 w-24"
                                  value={editForm.ora_inizio || ''} onChange={e => setEditForm(p => ({ ...p, ora_inizio: e.target.value }))} />
                                <select className="input text-xs py-1 px-2 w-32" value={editForm.mezzo_id || ''}
                                  onChange={e => setEditForm(p => ({ ...p, mezzo_id: e.target.value }))}>
                                  <option value="">Mezzo</option>
                                  {mezzi.map(m => <option key={m.id} value={m.id}>{m.targa}</option>)}
                                </select>
                                <button onClick={() => handleSaveEdit(a.id)} disabled={updateMut.isPending}
                                  className="text-xs bg-blue-50 text-blue-700 border border-blue-300 px-2 py-1 rounded hover:bg-blue-100">Salva</button>
                                <button onClick={() => { setEditingId(null); setEditForm({}) }}
                                  className="text-xs text-gray-500 hover:text-gray-700 px-1">Annulla</button>
                              </div>
                            ) : (
                              <>
                                <button title="Modifica" onClick={() => handleStartEdit(a)}
                                  className="text-xs bg-amber-50 text-amber-700 border border-amber-300 px-2 py-1 rounded hover:bg-amber-100 flex items-center gap-1">
                                  <Pencil size={13} />
                                </button>
                                {a.status === 'assegnato' && (
                                  <button onClick={() => updateStatusMut.mutate({ id: a.id, status: 'in_corso' })}
                                    className="text-xs bg-green-50 text-green-700 border border-green-300 px-2 py-1 rounded hover:bg-green-100">Avvia</button>
                                )}
                                {a.status === 'in_corso' && (
                                  <button onClick={() => updateStatusMut.mutate({ id: a.id, status: 'completato' })}
                                    className="text-xs bg-gray-50 text-gray-700 border border-gray-300 px-2 py-1 rounded hover:bg-gray-100">Completa</button>
                                )}
                                <button title="WhatsApp Tecnico" onClick={() => whatsappTecnicoMut.mutate(a.id)} disabled={whatsappTecnicoMut.isPending}
                                  className="text-xs bg-emerald-50 text-emerald-700 border border-emerald-300 px-2 py-1 rounded hover:bg-emerald-100 flex items-center gap-1">
                                  <MessageCircle size={13} /> Tecnico
                                </button>
                                <button title="WhatsApp Gruppo" onClick={() => whatsappGruppoMut.mutate(a.id)} disabled={whatsappGruppoMut.isPending}
                                  className="text-xs bg-teal-50 text-teal-700 border border-teal-300 px-2 py-1 rounded hover:bg-teal-100 flex items-center gap-1">
                                  <MessageCircle size={13} /> Gruppo
                                </button>
                                <button title="Email Tecnico" onClick={() => emailTecnicoMut.mutate(a.id)} disabled={emailTecnicoMut.isPending}
                                  className="text-xs bg-blue-50 text-blue-700 border border-blue-300 px-2 py-1 rounded hover:bg-blue-100 flex items-center gap-1">
                                  <Mail size={13} /> Email
                                </button>
                                <button onClick={() => confirmDelete('Eliminare?', () => deleteMut.mutate(a.id))}
                                  className="text-xs text-red-500 hover:text-red-700 px-1">✕</button>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )
                })}
              </>
            )}
          </div>
        )}
      </div>

      {/* Drag overlay */}
      <DragOverlay>
        {activeDrag ? (
          <div className="rounded border border-l-4 border-l-blue-400 bg-white shadow-xl p-2 w-48 opacity-90">
            <div className="text-xs font-semibold truncate">{activeDrag.commessa_nome}</div>
            <div className="text-[10px] text-gray-500">{activeDrag.cantiere_nome}</div>
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  )
}
